import Keycloak, { KeycloakTokenParsed } from 'keycloak-js'
import {
	PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'

import { env } from '@app/config/env'
import { type AdminOperationId } from '@shared/api'
import { getInterfaceErrorDescription } from '@shared/i18n/interface-messages'
import { createLocalPrincipal } from './local-principal'
import { hasOperationAccess } from './permissions'
import { AuthPrincipal, principalFromToken, TokenClaims } from './token-principal'

type ExtendedToken = KeycloakTokenParsed & TokenClaims

type AuthContextValue = {
	isReady: boolean
	isAuthenticated: boolean
	principal: AuthPrincipal | null
	hasAnyRole: (roles: string[]) => boolean
	can: (operationId: AdminOperationId) => boolean
	getAccessToken: () => Promise<string | null>
	logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

let keycloak: Keycloak | null = null
let initialization: Promise<boolean> | null = null

const getKeycloak = () => {
	if (!env.keycloakUrl || !env.keycloakRealm) {
		throw new Error('Keycloak configuration is incomplete')
	}
	if (!keycloak) {
		keycloak = new Keycloak({
			url: env.keycloakUrl,
			realm: env.keycloakRealm,
			clientId: env.keycloakClientId,
		})
	}
	return keycloak
}

const initializeKeycloak = () => {
	const instance = getKeycloak()
	if (!initialization) {
		initialization = instance.init({
			onLoad: 'login-required',
			pkceMethod: 'S256',
			checkLoginIframe: false,
		})
	}
	return initialization
}

export const AuthProvider = ({ children }: PropsWithChildren) => {
	const [isReady, setIsReady] = useState(!env.authEnabled)
	const [isAuthenticated, setIsAuthenticated] = useState(!env.authEnabled)
	const [principal, setPrincipal] = useState<AuthPrincipal | null>(
		env.authEnabled ? null : createLocalPrincipal(),
	)
	const [authError, setAuthError] = useState<string | null>(null)

	useEffect(() => {
		if (!env.authEnabled) {
			return
		}
		let active = true
		const instance = getKeycloak()
		initializeKeycloak()
			.then(authenticated => {
				if (!active) {
					return
				}
				setIsAuthenticated(authenticated)
				setPrincipal(principalFromToken(instance.tokenParsed as ExtendedToken, env.keycloakClientId))
				setIsReady(true)
			})
			.catch(error => {
				if (active) {
					setIsReady(true)
					setIsAuthenticated(false)
					setPrincipal(null)
					setAuthError(getInterfaceErrorDescription(error, 'Не удалось выполнить вход'))
				}
			})
		return () => {
			active = false
		}
	}, [])

	const getAccessToken = useCallback(async () => {
		if (!env.authEnabled) {
			return null
		}
		const instance = getKeycloak()
		await instance.updateToken(30)
		setPrincipal(principalFromToken(instance.tokenParsed as ExtendedToken, env.keycloakClientId))
		return instance.token ?? null
	}, [])

	const logout = useCallback(async () => {
		if (!env.authEnabled) {
			return
		}
		const instance = getKeycloak()
		await instance.logout({ redirectUri: window.location.origin })
	}, [])

	const hasAnyRole = useCallback(
		(roles: string[]) => {
			if (roles.length === 0) {
				return true
			}
			const assigned = new Set(principal?.roles ?? [])
			return roles.some(role => assigned.has(role))
		},
		[principal],
	)

	const can = useCallback(
		(operationId: AdminOperationId) =>
			hasOperationAccess(principal?.roles ?? [], operationId),
		[principal],
	)

	const value = useMemo<AuthContextValue>(
		() => ({
			isReady,
			isAuthenticated,
			principal,
			hasAnyRole,
			can,
			getAccessToken,
			logout,
		}),
		[isReady, isAuthenticated, principal, hasAnyRole, can, getAccessToken, logout],
	)

	if (authError) {
		return (
			<div className='flex min-h-screen items-center justify-center bg-background p-6'>
				<div className='max-w-lg text-center'>
					<h1 className='text-xl font-semibold'>Ошибка аутентификации</h1>
					<p className='mt-2 text-sm text-muted-foreground'>{authError}</p>
				</div>
			</div>
		)
	}

	if (!isReady) {
		return (
			<div className='flex min-h-screen items-center justify-center bg-background'>
				<div className='text-sm text-muted-foreground'>Проверка доступа…</div>
			</div>
		)
	}

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
	const value = useContext(AuthContext)
	if (!value) {
		throw new Error('useAuth must be used inside AuthProvider')
	}
	return value
}
